<script setup lang="ts">
import {computed, onBeforeUpdate, onMounted, ref} from "vue";
import {IonIcon} from "@ionic/vue";
import {
    needsAction,
    ObjectiveGroup,
    objectiveSubjectToGroupTitleMap, sortObjectiveGroupContentDependingOnSubject, sortObjectiveGroups,
    useAInesObjectiveStore
} from "@/helper/chat/ainesObjectives";
import {AInesObjective, AInesObjectiveState, AInesObjectiveSubject} from "@/graphql/generated/graphql";
import RichResponseListItem from "./RichResponses/components/RichResponseListItem.vue";
import date from "@/helper/datetime/date";
import Skeleton from "@/components/Skeleton.vue";
import {iconType2Icon} from "@/helper/icon";
import {getVerticalDifferencInPx, isElementBottomOutOfBoundsInScrollContainer} from '@/helper/dom'
import Transitionator from "../components/transitions/Transitionator.vue";
import CalendarDetail from "@/views/CalendarDetail/CalendarDetail.vue";
import {kapitelDateString} from "@/graphql/kapitelTypes";
import {consoleErrorApp} from "@/helper/console";
import AInesObjectivesListItem from "@/views/Chat/AInesObjectivesListItem.vue";

const props = defineProps({
    blur: {
        type: Boolean,
        required: false,
        default: false
    }
})


const emit = defineEmits(["objective:click", "bookingSuggestion:open"]);
const isLoading = computed(() => store.loading)
const itemRefs = ref([] as any[])
const isActionNeededScrollHintVisible = ref(false)
const scrollElement = ref()
const dayDetailDate = ref<kapitelDateString | undefined>(undefined)

const setItemRef = (comp:any) => comp ? itemRefs.value.push(comp) : void 0

const store = useAInesObjectiveStore();



onMounted(async () => {
    await store.refetch();

    requestAnimationFrame(() => {
        updateNeedsActionScrollHint();
    })
});

onBeforeUpdate(() => {
    itemRefs.value = []
})

const handleObjectiveClick = (objective: AInesObjective) => {
    if (objective.clickAction?.type === 'openModal') {
        if (objective.subject === AInesObjectiveSubject.BookingSuggestion) {
            emit('bookingSuggestion:open')
        } else {
            consoleErrorApp("unknown objective modal " + objective.subject)
        }
    }else {
        emit('objective:click', objective)
    }
}


const objectivesGrouped = computed(() => {
    const groups = Object.values(store.objectives.reduce((res, objective) => {
        const title = objectiveSubjectToGroupTitleMap[objective.subject]
        if (!title) {
            debugger
            console.warn('missing title for objective subject %o', objective.subject)
            return res
        }
        if (!res[title]) {
            res[title] = {
                subject: objective.subject,
                title: title,
                objectives: [] as AInesObjective[]
            } as ObjectiveGroup
        }

        res[title].objectives.push(objective)

        return res
    }, {} as {[key: string]: ObjectiveGroup}))

    sortObjectiveGroups(groups)

    groups.map(sortObjectiveGroupContentDependingOnSubject)

    return groups
})

const getFirstInVisibleNeedsActionItem = () => {
    return itemRefs.value.find(itemComponent => {
        return itemComponent.props?.badge &&
            isElementBottomOutOfBoundsInScrollContainer(itemComponent.$el, scrollElement.value as HTMLElement)
    })
}

const updateNeedsActionScrollHint = () => {
    const isVisible = getFirstInVisibleNeedsActionItem() !== undefined
    isActionNeededScrollHintVisible.value = isVisible
}

const onClickScrollToAction = () => {
    const item = getFirstInVisibleNeedsActionItem()

    if (!item) {
        return
    }

    const delta = getVerticalDifferencInPx(item.$el, scrollElement.value)
    scrollElement.value.scrollTo({top: scrollElement.value.scrollTop + delta, behavior: 'smooth'});
}

defineExpose({
    updateNeedsActionScrollHint
})

</script>

<template>
    <div class="aines-container transparent no-padding-top no-padding-bottom" :class="{blur: props.blur}">
        <div ref="scrollElement" class="scroller app-padding-left app-padding-right" @scroll="updateNeedsActionScrollHint" >
            <h1 class="custom-ricarda">
                
                Aktuelles
                <img src="/logo.png" />
            </h1>
            <Skeleton
                v-if="isLoading"
                :items="['margin', 'title', 'block--height-6em', 'block--height-6em', 'block--height-6em']"
            />
            <template v-else>
                <template v-for="group in objectivesGrouped">
                    <h2 class="globalHeader" :data-cy="'objective '+ group.title">{{ group.title }}</h2>
                    <AInesObjectivesListItem
                        v-for="objective in group.objectives"
                        :key="objective.reference"
                        :objective="objective"
                        :ref="setItemRef"
                        @objective:click="handleObjectiveClick"
                    ></AInesObjectivesListItem>
                </template>
            </template>
        </div>
        <Transitionator :types="['opacity', 'scale']" easing="easeOutBounce">
            <div
                v-if="isActionNeededScrollHintVisible"
                class="needs-action-hint-button floating-big-pseudo overlaying-other-elements"
                @click="onClickScrollToAction"
                style="transform: translate(-50%, -4em)"
            >
                Weitere
                <ion-icon src="/icons/caret-down.svg" />
            </div>
        </Transitionator>
        <CalendarDetail v-if="dayDetailDate" v-model="dayDetailDate" />
    </div>
</template>

<style scoped lang="scss">
.aines-container {
    transition: filter 0.3s;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    
    > .scroller {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        overflow-x: hidden;
        overflow-y: auto;

        padding-bottom: 8em;

    }

    .needs-action-hint-button {
        position: absolute;
        top: 100%;
        left: 50%;
        background-color: var(--ion-color-red-tint-3);
        color: var(--ion-color-primary);
        z-index: 300;
        // transform: translate(-50%, -4em);
        padding: 0.5em;
        border-radius: 1em;
        font-weight: var(--custom-font-weight-medium);
        display: flex;
        align-items: center;

        ion-icon {
            margin-left: 0.3em;
        }
    }

    h1 {
        margin-bottom: 0.5em;
        display: flex;
        align-items: center;
        margin-left: -0.1em;
        margin-top: 0.25em;

        img {
            $SIZE: 0.6em;
            width: $SIZE;
            height: $SIZE;
            margin-left: 0.15em;
            margin-right: 0.25em;
            margin-top: -0.5em;
        }
    }

    ion-item {
        margin-bottom: 1em;
    }

    &.blur {
        filter: blur(0.5em);

        &,
        * {
            pointer-events: none;
        }
    }
}


</style>
